// src/services/manage_api.js
import { apiClient } from "./api_client";

const manageApi = {
  addUser: async (user) => {
    try {
      const response = await apiClient.post("/manage/add-user", user);
      return response.data;
    } catch (error) {
      console.error("Error adding user:", error);
      throw error;
    }
  },
  editUser: async (user) => {
    try {
      const response = await apiClient.put(`/manage/update-user`, user);
      return response.data;
    } catch (error) {
      console.error("Error editing user:", error);
      throw error;
    }
  },
  /**
   * Archive state for one account: whether it is archived, whether it can be
   * put back, and what else has been archived away from the same address.
   * One call, made when the edit form opens.
   */
  getArchiveInfo: async (email) => {
    try {
      const response = await apiClient.get(
        `/manage/archive-info?email=${encodeURIComponent(email)}`,
      );
      return response.data;
    } catch (error) {
      console.error("Error getting archive info:", error);
      throw error;
    }
  },
  /**
   * Move an account off its address so a new signup can take it. Does not
   * create the replacement account; that stays with the add-user flows.
   */
  archiveUser: async (email) => {
    try {
      const response = await apiClient.post(`/manage/archive-user`, { email });
      return response.data;
    } catch (error) {
      console.error("Error archiving user:", error);
      throw error;
    }
  },
  /**
   * Archive or unarchive a batch of addresses.
   * With dryRun the server classifies every row and computes where each
   * account would land, writing nothing. Executing recomputes rather than
   * replaying the preview, because the database moves between the two calls,
   * so compare result_email against what was previewed.
   */
  bulkArchive: async ({ emails, mode, dryRun }) => {
    try {
      const response = await apiClient.post(`/manage/archive-users`, {
        emails,
        mode,
        dry_run: dryRun,
      });
      return response.data;
    } catch (error) {
      console.error("Error running bulk archive:", error);
      throw error;
    }
  },
  /** Put an archived account back on the address it gave up. */
  unarchiveUser: async (email) => {
    try {
      const response = await apiClient.post(`/manage/unarchive-user`, { email });
      return response.data;
    } catch (error) {
      console.error("Error unarchiving user:", error);
      throw error;
    }
  },
  /**
   * Reset an existing account's password and re-send the onboarding email
   * with it. For when the original onboarding email never arrived.
   */
  resendOnboardingEmail: async (email, newPassword) => {
    try {
      const response = await apiClient.post(`/manage/resend-onboarding-email`, {
        email,
        new_password: newPassword,
      });
      return response.data;
    } catch (error) {
      console.error("Error resending onboarding email:", error);
      throw error;
    }
  },
  deleteUser: async (user) => {
    try {
      const response = await apiClient.delete(`/manage/delete-user`, {
        data: user,
      });
      return response.data;
    } catch (error) {
      console.error("Error deleting user:", error);
      throw error;
    }
  },
  getUser: async (userId) => {
    try {
      const response = await apiClient.get(`/search-users/${userId}`);
      return response.data;
    } catch (error) {
      console.error("Error getting user details:", error);
      //throw error;
      return [
        {
          userId: "1",
          email: "user4@example.com",
          userTypes: ["admin"],
          userGroup: "civilian",
          createDate: "2021-01-01",
        },
      ];
    }
  },
  /**
   * Bulk add multiple users
   * @param {Array} usersData - Array of user objects to create
   * @returns {Promise} Promise with bulk creation result
   */
  async addUsers(usersData, options = {}) {
    console.log("Adding users:", usersData, options);
    try {
      const payload = {
        users: usersData,
        ...options, // e.g., { send_notifications: true }
      };

      const response = await apiClient.post(
        "/manage/add-users",
        payload
      );
      return response.data;
    } catch (error) {
      console.error("Error bulk adding users:", error);

      // Return mock response for development
      // return new Promise((resolve) => {
      //   setTimeout(() => {
      //     resolve({
      //       success: true,
      //       message: "Bulk upload completed successfully (mock response)",
      //       created_count: usersData.length,
      //       failed_count: 0,
      //       errors: [],
      //     });
      //   }, 1500);
      // });
      throw error;
    }
  },
  getUsers: async (query) => {
    try {
      let url = "/manage/search-users";
      if (query) {
        const queryString = new URLSearchParams(query).toString();
        // example query: {'email': 'test', 'role': 'user', 'user_id': 1}
        // example url query ?email=test&role=user&user_id=1
        url += `?${queryString}`;
      }
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      console.error("Error getting users:", error);
      //throw error;
      return {
        users: [
          {
            userId: "1",
            email: "user4@example.com",
            userTypes: ["admin", "mentor", "mentee"],
            userGroup: "civilian",
            createDate: "2021-01-01",
          },
          {
            userId: "2",
            email: "user2@example.com",
            userTypes: ["user"],
            userGroup: "civilian",
            createDate: "2021-02-01",
          },
          {
            userId: "3",
            email: "user3@example.com",
            userTypes: ["user"],
            userGroup: "civilian",
            createDate: "2021-03-01",
          },
          {
            userId: "4",
            email: "user4@example.com",
            userTypes: ["user"],
            userGroup: "civilian",
            createDate: "2021-03-01",
          },
          {
            userId: "5",
            email: "user5@example.com",
            userTypes: ["user"],
            userGroup: "civilian",
            createDate: "2021-03-01",
          },
          {
            userId: "6",
            email: "user6@example.com",
            userTypes: ["user"],
            userGroup: "civilian",
            createDate: "2021-03-01",
          },
          {
            userId: "7",
            email: "user7@example.com",
            userTypes: ["user"],
            userGroup: "civilian",
            createDate: "2021-03-01",
          },
          {
            userId: "8",
            email: "user8@example.com",
            userTypes: ["user"],
            userGroup: "civilian",
            createDate: "2021-03-01",
          },
          {
            userId: "9",
            email: "user9@example.com",
            userTypes: ["user"],
            userGroup: "civilian",
            createDate: "2021-03-01",
          },
          {
            userId: "10",
            email: "user10@example.com",
            userTypes: ["user"],
            userGroup: "civilian",
            createDate: "2021-03-01",
          },
        ],
      };
    }
  },
  getMentees: async (mentorId) => {
    try {
      const response = await apiClient.get(`/search-users/${mentorId}`);
      return response.data;
    } catch (error) {
      console.error("Error getting mentees:", error);
      // throw error;
      return [
        {
          userId: "1",
          email: "user4@example.com",
          userTypes: ["admin"],
          userGroup: "civilian",
          createDate: "2021-01-01",
        },
        {
          userId: "2",
          email: "user2@example.com",
          userTypes: ["user"],
          userGroup: "civilian",
          createDate: "2021-02-01",
        },
        {
          userId: "3",
          email: "user3@example.com",
          userTypes: ["user"],
          userGroup: "civilian",
          createDate: "2021-03-01",
        },
        {
          userId: "4",
          email: "user6@example.com",
          userTypes: ["user"],
          userGroup: "civilian",
          createDate: "2021-05-01",
        },
        {
          userId: "5",
          email: "user5@example.com",
          userTypes: ["mentor"],
          userGroup: "military",
          createDate: "2021-02-05",
        },
        {
          userId: "6",
          email: "user7@example.com",
          userTypes: ["mentor"],
          userGroup: "civilian",
          createDate: "2021-06-01",
        },
        {
          userId: "1",
          email: "mentee1@example.com",
          userTypes: ["mentee"],
          userGroup: "civilian",
          createDate: "2021-01-01",
        },
        {
          userId: "2",
          email: "mentee2@example.com",
          userTypes: ["mentee"],
          userGroup: "civilian",
          createDate: "2021-02-01",
        },
        {
          userId: "3",
          email: "mentee3@example.com",
          userTypes: ["mentee"],
          userGroup: "civilian",
          createDate: "2021-03-01",
        },
        {
          userId: "4",
          email: "useweewer4@example.com",
          userTypes: ["admin"],
          userGroup: "civilian",
          createDate: "2021-01-01",
        },
        {
          userId: "5",
          email: "usbher2@example.com",
          userTypes: ["user"],
          userGroup: "civilian",
          createDate: "2021-02-01",
        },
        {
          userId: "6",
          email: "ucseqqqr3@example.com",
          userTypes: ["user"],
          userGroup: "civilian",
          createDate: "2021-03-01",
        },
        {
          userId: "7",
          email: "userfc6@example.com",
          userTypes: ["user"],
          userGroup: "civilian",
          createDate: "2021-05-01",
        },
        {
          userId: "8",
          email: "uscer5@example.com",
          userTypes: ["mentor"],
          userGroup: "civilian",
          createDate: "2021-02-05",
        },
        {
          userId: "9",
          email: "ubcfser7@example.com",
          userTypes: ["mentor"],
          userGroup: "civilian",
          createDate: "2021-06-01",
        },
        {
          userId: "6",
          email: "usecvr3@example.com",
          userTypes: ["user"],
          userGroup: "civilian",
          createDate: "2021-03-01",
        },
        {
          userId: "7",
          email: "usecr6@example.com",
          userTypes: ["user"],
          userGroup: "civilian",
          createDate: "2021-05-01",
        },
        {
          userId: "8",
          email: "user5@example.com",
          userTypes: ["mentor"],
          userGroup: "civilian",
          createDate: "2021-02-05",
        },
        {
          userId: "9",
          email: "uscghder7@example.com",
          userTypes: ["mentor"],
          userGroup: "civilian",
          createDate: "2021-06-01",
        },
        {
          userId: "6",
          email: "usfdger3@example.com",
          userTypes: ["user"],
          userGroup: "civilian",
          createDate: "2021-03-01",
        },
        {
          userId: "7",
          email: "ussdfsgfver6@example.com",
          userTypes: ["user"],
          userGroup: "civilian",
          createDate: "2021-05-01",
        },
        {
          userId: "8",
          email: "usyjjger5@example.com",
          userTypes: ["mentor"],
          userGroup: "civilian",
          createDate: "2021-02-05",
        },
        {
          userId: "9",
          email: "usgtsrfser7@example.com",
          userTypes: ["mentor"],
          userGroup: "civilian",
          createDate: "2021-06-01",
        },
        {
          userId: "6",
          email: "usser3@example.com",
          userTypes: ["user"],
          userGroup: "civilian",
          createDate: "2021-03-01",
        },
        {
          userId: "7",
          email: "fus33er6@example.com",
          userTypes: ["user"],
          userGroup: "civilian",
          createDate: "2021-05-01",
        },
        {
          userId: "8",
          email: "ufse44r5@example.com",
          userTypes: ["mentor"],
          userGroup: "civilian",
          createDate: "2021-02-05",
        },
        {
          userId: "9",
          email: "user7@example.com",
          userTypes: ["mentor"],
          userGroup: "civilian",
          createDate: "2021-06-01",
        },
        {
          userId: "6",
          email: "us2er3@example.com",
          userTypes: ["user"],
          userGroup: "civilian",
          createDate: "2021-03-01",
        },
        {
          userId: "7",
          email: "use2r6@example.com",
          userTypes: ["user"],
          userGroup: "civilian",
          createDate: "2021-05-01",
        },
        {
          userId: "8",
          email: "use23r5@example.com",
          userTypes: ["mentor"],
          userGroup: "civilian",
          createDate: "2021-02-05",
        },
        {
          userId: "9",
          email: "us23er7@example.com",
          userTypes: ["mentor"],
          userGroup: "civilian",
          createDate: "2021-06-01",
        },
        {
          userId: "6",
          email: "user323@example.com",
          userTypes: ["user"],
          userGroup: "civilian",
          createDate: "2021-03-01",
        },
        {
          userId: "7",
          email: "user26@example.com",
          userTypes: ["user"],
          userGroup: "civilian",
          createDate: "2021-05-01",
        },
        {
          userId: "8",
          email: "user52@example.com",
          userTypes: ["mentor"],
          userGroup: "civilian",
          createDate: "2021-02-05",
        },
        {
          userId: "9",
          email: "user37@example.com",
          userTypes: ["mentor"],
          userGroup: "civilian",
          createDate: "2021-06-01",
        },
      ];
    }
  },
  getGroups: async (detail = false) => {
    try {
      const { data } = await apiClient.get("/group/list", {
        params: { detail }, // /group/list?detail=true|false
      });
      return data;
    } catch (error) {
      console.error("Error listing groups:", error);
      throw error;
    }
  },
};

export default manageApi;
